import { useState, useEffect, useMemo } from 'react';
import { Eye, EyeOff, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';
import type { FileType } from '@/routes/chat/hooks/use-chat';
import clsx from 'clsx';

// Import markdown parser
import { marked } from 'marked';

export interface DocumentPreviewProps {
  file: FileType;
  isVisible: boolean;
  onToggleVisibility: () => void;
  autoRefresh?: boolean;
  onAutoRefreshChange?: (enabled: boolean) => void;
  className?: string;
}

export interface PreviewMode {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

export function DocumentPreview({
  file,
  isVisible,
  onToggleVisibility,
  autoRefresh = true,
  onAutoRefreshChange,
  className
}: DocumentPreviewProps) {
  const [previewMode, setPreviewMode] = useState<'split' | 'preview-only' | 'editor-only'>('split');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // Check if file type supports preview
  const supportsPreview = useMemo(() => {
    const ext = file.filePath.split('.').pop()?.toLowerCase();
    return ['md', 'markdown', 'html', 'htm', 'txt', 'json'].includes(ext || '');
  }, [file.filePath]);

  // Generate preview content
  const previewContent = useMemo(() => {
    if (!supportsPreview) return null;

    const ext = file.filePath.split('.').pop()?.toLowerCase();
    const content = file.fileContents;

    try {
      switch (ext) {
        case 'md':
        case 'markdown':
          // Configure marked for better features
          marked.setOptions({
            breaks: true,
            gfm: true,
          });
          return marked(content) as string;

        case 'html':
        case 'htm':
          return content;

        case 'json':
          // Pretty print JSON
          try {
            const parsed = JSON.parse(content);
            return `<pre><code>${JSON.stringify(parsed, null, 2)}</code></pre>`;
          } catch {
            return `<pre><code>${content}</code></pre>`;
          }

        case 'txt':
          return `<pre><code>${content}</code></pre>`;

        default:
          return `<pre><code>${content}</code></pre>`;
      }
    } catch (error) {
      return `<div class="text-red-500">Error rendering preview: ${error instanceof Error ? error.message : 'Unknown error'}</div>`;
    }
  }, [file.fileContents, file.filePath, supportsPreview]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setLastRefresh(Date.now());
    }, 1000); // Refresh every second when auto-refresh is enabled

    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Manual refresh
  const handleManualRefresh = () => {
    setLastRefresh(Date.now());
  };

  // Toggle fullscreen
  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!supportsPreview || !isVisible) {
    return null;
  }

  return (
    <div
      className={clsx(
        'flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700',
        {
          'fixed inset-0 z-50': isFullscreen,
          'w-1/2': previewMode === 'split' && !isFullscreen,
          'w-full': previewMode === 'preview-only' || isFullscreen,
        },
        className
      )}
    >
      {/* Preview Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-2">
          <Eye className="size-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Preview: {file.filePath.split('/').pop()}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Auto-refresh toggle */}
          <button
            onClick={() => onAutoRefreshChange?.(!autoRefresh)}
            className={clsx(
              'px-2 py-1 text-xs rounded transition-colors',
              autoRefresh
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
            )}
            title={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'}
          >
            Auto
          </button>

          {/* Manual refresh */}
          <button
            onClick={handleManualRefresh}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="Refresh preview"
          >
            <RefreshCw className="size-4 text-gray-600 dark:text-gray-400" />
          </button>

          {/* Preview mode selector */}
          <select
            value={previewMode}
            onChange={(e) => setPreviewMode(e.target.value as typeof previewMode)}
            className="text-xs bg-transparent border border-gray-300 dark:border-gray-600 rounded px-2 py-1"
          >
            <option value="split">Split View</option>
            <option value="preview-only">Preview Only</option>
            <option value="editor-only">Editor Only</option>
          </select>

          {/* Fullscreen toggle */}
          <button
            onClick={handleToggleFullscreen}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="size-4 text-gray-600 dark:text-gray-400" />
            ) : (
              <Maximize2 className="size-4 text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {/* Close preview */}
          <button
            onClick={onToggleVisibility}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="Close preview"
          >
            <EyeOff className="size-4 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-auto p-4">
        {previewContent ? (
          <div
            className="prose prose-sm max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: previewContent }}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <Eye className="size-8 mx-auto mb-2 opacity-50" />
              <p>No preview available for this file type</p>
            </div>
          </div>
        )}
      </div>

      {/* Preview Footer */}
      <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            Last updated: {new Date(lastRefresh).toLocaleTimeString()}
          </span>
          <span>
            {file.fileContents.length} characters
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook for managing document preview state
 */
export function useDocumentPreview() {
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [previewMode, setPreviewMode] = useState<'split' | 'preview-only' | 'editor-only'>('split');

  const togglePreview = () => {
    setIsPreviewVisible(!isPreviewVisible);
  };

  const showPreview = () => {
    setIsPreviewVisible(true);
  };

  const hidePreview = () => {
    setIsPreviewVisible(false);
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  // Check if a file supports preview
  const supportsPreview = (filePath: string) => {
    const ext = filePath.split('.').pop()?.toLowerCase();
    return ['md', 'markdown', 'html', 'htm', 'txt', 'json'].includes(ext || '');
  };

  return {
    isPreviewVisible,
    autoRefresh,
    previewMode,
    togglePreview,
    showPreview,
    hidePreview,
    toggleAutoRefresh,
    setAutoRefresh,
    setPreviewMode,
    supportsPreview
  };
}
